/**
 * Workflow definitions — the approval flows an organisation has configured, as a contract.
 *
 * ADR 0010 section 9: "a `payroll_run` workflow definition is ... editable through an admin screen
 * backed by contract routes for workflow definitions". This is that contract, and it is generic:
 * a definition belongs to an `entityType` (`payroll_run`, `leave_request`, ...), never to payroll.
 * `@hrms/workflow` stays the one engine (CLAUDE.md invariant 6); these routes only let an
 * administrator read and change what the engine is told to run.
 *
 * VERSIONED, NEVER EDITED IN PLACE. A definition is one row per version, and an in-flight approval
 * keeps resolving against the version it started under. `updateDefinition` therefore never rewrites
 * a row: it writes the NEXT version of the same lineage — (entityType, legalEntityId) — and retires
 * the previous one by clearing `isActive`. `:definitionId` names the version the caller LOADED, and
 * that is the optimistic-concurrency token: if a newer version exists the write is refused with a
 * 409 rather than silently overwriting somebody else's edit (`workflowDefinitionConflictSchema`).
 * An update that changes nothing writes nothing and returns the current version.
 *
 * WHO MAY CALL. Reading is `workflow:read` and writing is `workflow:manage`, both checked by the
 * handler against the ORGANISATION — a definition is organisation configuration, so a self-scoped
 * grant (every employee holds `workflow:read` at self scope) never opens it. `validateDefinition`
 * is a dry run that stores nothing; it needs `workflow:manage` because only an author has a use
 * for it.
 *
 * THE COPILOT (ADR 0009). `metadata.permission` is what makes a route a tool. `list`, `get`,
 * `coverage` and `entity-types` are GETs, so they are read tools; `createDefinition`,
 * `updateDefinition` and the deactivate/reactivate POSTs are writes, so the copilot can only
 * PROPOSE them and they execute on the person's click as them. `validateDefinition` is a POST that
 * writes nothing — a non-GET would be offered to the copilot as a write proposal, which is noise
 * for a dry run, so it is `ai: 'hidden'` exactly as `payrollRules.simulate` is.
 *
 * ROUTE ORDER. `coverage`, `entity-types` and `validate` are static segments and are declared
 * before `/:definitionId` so they are never captured as an id.
 *
 * ROLE APPROVERS ARE KEYS. A `role` step names a role by its key — built-in or the organisation's
 * own custom role. The schema checks the key's shape only; the API checks it exists in the
 * organisation's `roles` table (see `workflowRoleKeySchema`).
 *
 * DRIFT GUARD. The step and condition schemas here mirror the engine's own
 * (`packages/workflow/src/types.ts`), which this package cannot import. The two are held together
 * by `packages/workflow/src/definition.test.ts`, which feeds both the same samples.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  APPROVER_RULES,
  PAYROLL_DIAGNOSTIC_CLASSES,
  PAYROLL_DIAGNOSTIC_SEVERITIES,
} from '../enums.js';
import {
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const authErrors = { 401: errorBodySchema, 403: errorBodySchema } as const;

// ---------------------------------------------------------------------------
// The definition language, as it crosses the wire
// ---------------------------------------------------------------------------

/**
 * The comparison vocabulary. Deliberately small and not an expression evaluator — a definition is
 * tenant-supplied data, and an evaluator would be a code-execution surface. Same ten operators as
 * the engine's `PREDICATE_OPERATORS`.
 */
export const WORKFLOW_PREDICATE_OPERATORS = [
  'eq',
  'ne',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'nin',
  'contains',
  'exists',
] as const;
export type WorkflowPredicateOperator = (typeof WORKFLOW_PREDICATE_OPERATORS)[number];

const scalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export const workflowPredicateValueSchema = z.union([scalarSchema, z.array(scalarSchema)]);

export const workflowComparisonSchema = z.object({
  /** A dot path resolved against the context frozen onto the instance, e.g. `isOrgOwner`. */
  field: z.string().min(1).max(120),
  op: z.enum(WORKFLOW_PREDICATE_OPERATORS),
  value: workflowPredicateValueSchema.optional(),
});

export type WorkflowPredicate =
  | z.infer<typeof workflowComparisonSchema>
  | { all: WorkflowPredicate[] }
  | { any: WorkflowPredicate[] }
  | { not: WorkflowPredicate };

export const workflowPredicateSchema: z.ZodType<WorkflowPredicate> = z.lazy(() =>
  z.union([
    workflowComparisonSchema,
    z.object({ all: z.array(workflowPredicateSchema).min(1) }),
    z.object({ any: z.array(workflowPredicateSchema).min(1) }),
    z.object({ not: workflowPredicateSchema }),
  ]),
);

/** Rules that cannot resolve without a role name or a person id. `cost_centre_owner` may pin one. */
const RULES_NEEDING_TARGET: ReadonlySet<string> = new Set(['role', 'specific_person']);

const ROLE_KEY_PATTERN = /^[a-z0-9_]+$/;

/**
 * A role, BY KEY — `hr_admin`, or an organisation's own `sales_lead`. The same shape a role is
 * created with (`createRoleSchema.name`), and deliberately NOT an enum of the built-in roles: a
 * custom role must be able to approve. Whether the key names a role that exists is a question
 * about the tenant, which a schema cannot answer; the API checks it against the organisation's
 * `roles` table (built-in and custom) when a definition is validated, created or saved, and
 * refuses an unknown one with `unknown_role`.
 */
export const workflowRoleKeySchema = z
  .string()
  .min(2)
  .max(64)
  .regex(ROLE_KEY_PATTERN, 'A role key is lower-case letters, digits and underscores');

/**
 * One approval step. `stepNo` is the ORDER KEY, not an array index: steps sharing a number run in
 * parallel, and a gap (10/20/30) lets a step be inserted later without renumbering.
 */
export const workflowStepSpecSchema = z
  .object({
    stepNo: z.number().int().min(1).max(999),
    approverRule: z.enum(APPROVER_RULES),
    /** A role KEY for `role`, a person id for `specific_person`, a cost-centre id otherwise. */
    roleOrPersonId: z.string().min(1).max(64).nullish(),
    parallel: z.boolean().default(false),
    autoApproveIf: workflowPredicateSchema.nullish(),
    /**
     * When the rule reaches nobody (no manager, top of the chart), hand the step to a holder of this
     * role — or of the first of these roles that has a holder. Role KEYS, built-in or custom. Absent
     * means an unassignable step stays unassigned. Declared here so a definition that carries one
     * survives a load-and-save through this contract instead of being silently stripped.
     */
    fallbackRole: z
      .union([workflowRoleKeySchema, z.array(workflowRoleKeySchema).min(1).max(10)])
      .nullish(),
    /** Hours before the step is overdue and escalates. Capped at 90 days. */
    slaHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 90)
      .nullish(),
    /** A separate, longer backstop: total hours of silence — including after escalation — before
     * the step auto-approves rather than blocking the requester forever. Independent of `slaHours`;
     * unset means no backstop. Capped at 90 days, same as `slaHours`. */
    autoApproveAfterHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 90)
      .nullish(),
  })
  .superRefine((step, ctx) => {
    if (RULES_NEEDING_TARGET.has(step.approverRule) && !step.roleOrPersonId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['roleOrPersonId'],
        message: `approverRule '${step.approverRule}' requires roleOrPersonId`,
      });
      return;
    }
    if (step.approverRule === 'role' && !ROLE_KEY_PATTERN.test(step.roleOrPersonId as string)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['roleOrPersonId'],
        message: `'${String(step.roleOrPersonId)}' is not a role key`,
      });
    }
  });
export type WorkflowStepSpec = z.infer<typeof workflowStepSpecSchema>;

/** A gate that reshapes the chain: "adds a step", "skips a step". Neither = does nothing = refused. */
export const workflowConditionSchema = z
  .object({
    when: workflowPredicateSchema,
    addSteps: z.array(workflowStepSpecSchema).max(50).default([]),
    skipSteps: z.array(z.number().int().min(1).max(999)).max(50).default([]),
  })
  .refine(
    (condition) => condition.addSteps.length > 0 || condition.skipSteps.length > 0,
    'a condition that neither adds nor skips a step does nothing',
  );
export type WorkflowCondition = z.infer<typeof workflowConditionSchema>;

/** Entity types are snake_case slugs: `payroll_run`, `leave_request`, `salary_component_change`. */
export const workflowEntityTypeSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/, 'An entity type is a lower-case snake_case name');

export const workflowDefinitionNameSchema = z.string().trim().min(1).max(120);

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

export const workflowDefinitionSchema = z.object({
  /** The id of THIS VERSION. An update returns a new id — see the header. */
  id: uuidSchema,
  entityType: workflowEntityTypeSchema,
  /** Per lineage, starting at 1. */
  version: z.number().int().min(1),
  name: z.string(),
  steps: z.array(workflowStepSpecSchema),
  conditions: z.array(workflowConditionSchema),
  /** Null = the whole organisation. A legal entity's own definition beats the organisation-wide one. */
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  /** The engine starts an approval from an ACTIVE version only. */
  isActive: z.boolean(),
  /** True for the highest version of its lineage — the only one an update may be based on. */
  isLatest: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type WorkflowDefinition = z.infer<typeof workflowDefinitionSchema>;

export const workflowDefinitionParamsSchema = z.object({ definitionId: uuidSchema });

const boolQuery = z.enum(['true', 'false']).transform((v) => v === 'true');

export const listWorkflowDefinitionsQuerySchema = z.object({
  entityType: workflowEntityTypeSchema.optional(),
  /** Restrict to one legal entity's definitions. Omit for every scope. */
  legalEntityId: uuidSchema.optional(),
  /**
   * Off (the default) returns the LATEST version of each lineage. On returns every version, oldest
   * last — the history an audit or a "what governed March" question needs.
   */
  includeHistory: boolQuery.optional(),
});

export const listWorkflowDefinitionsResponseSchema = z.object({
  items: z.array(workflowDefinitionSchema),
});

export const updateWorkflowDefinitionBodySchema = z.object({
  name: workflowDefinitionNameSchema,
  steps: z.array(workflowStepSpecSchema).min(1).max(50),
  conditions: z.array(workflowConditionSchema).max(50).default([]),
  /** Defaults to the current state. `false` retires the lineage: the engine will find no flow. */
  isActive: z.boolean().optional(),
});
export type UpdateWorkflowDefinitionBody = z.infer<typeof updateWorkflowDefinitionBodySchema>;

/**
 * `details` of a 409 from `updateDefinition`. `superseded` = a newer version exists, so the caller
 * edited a stale copy; reload `latestDefinitionId` and re-apply the change.
 */
export const workflowDefinitionConflictSchema = z.object({
  reason: z.literal('superseded'),
  latestDefinitionId: uuidSchema,
  latestVersion: z.number().int().min(1),
});
export type WorkflowDefinitionConflict = z.infer<typeof workflowDefinitionConflictSchema>;

/**
 * Start a NEW lineage — (entityType, legalEntityId) — at version 1. There is at most one lineage per
 * pair: creating one that already exists is a 409 carrying `workflowDefinitionExistsSchema`, and the
 * caller edits that lineage with `updateDefinition` instead. That keeps "which flow governs leave
 * for Acme India" a question with one answer.
 */
export const createWorkflowDefinitionBodySchema = z.object({
  entityType: workflowEntityTypeSchema,
  /** Null = the whole organisation. */
  legalEntityId: uuidSchema.nullable().default(null),
  name: workflowDefinitionNameSchema,
  steps: z.array(workflowStepSpecSchema).min(1).max(50),
  conditions: z.array(workflowConditionSchema).max(50).default([]),
  /** Defaults to active: the engine uses it for the next approval of this type. */
  isActive: z.boolean().default(true),
});
export type CreateWorkflowDefinitionBody = z.infer<typeof createWorkflowDefinitionBodySchema>;

/** `details` of a 409 from `createDefinition`: the lineage exists, so edit its latest version. */
export const workflowDefinitionExistsSchema = z.object({
  reason: z.literal('lineage_exists'),
  latestDefinitionId: uuidSchema,
  latestVersion: z.number().int().min(1),
});
export type WorkflowDefinitionExists = z.infer<typeof workflowDefinitionExistsSchema>;

// ---------------------------------------------------------------------------
// Validation (a dry run)
// ---------------------------------------------------------------------------

/**
 * `error` means the definition cannot be saved (or would fail when an approval starts). `warning`
 * means it is legal but probably not what was meant — a step that routes to a role that cannot
 * decide, a condition that skips a step nothing defines.
 */
export const workflowDefinitionIssueSchema = z.object({
  severity: z.enum(['error', 'warning']),
  /** Stable, machine-readable. e.g. `invalid_definition`, `unknown_context_field`, `role_cannot_decide`. */
  code: z.string(),
  /** Where in the submitted definition, e.g. `['steps', 0, 'roleOrPersonId']`. */
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
});
export type WorkflowDefinitionIssue = z.infer<typeof workflowDefinitionIssueSchema>;

/**
 * Deliberately LOOSE: a structural mistake must come back as an issue in a 200, not as a 422 from
 * the adapter, or a form could never show more than "the body was invalid".
 */
export const validateWorkflowDefinitionBodySchema = z.object({
  entityType: workflowEntityTypeSchema,
  legalEntityId: uuidSchema.nullable().default(null),
  steps: z.array(z.unknown()).max(50),
  conditions: z.array(z.unknown()).max(50).default([]),
});

export const workflowDefinitionValidationSchema = z.object({
  valid: z.boolean(),
  issues: z.array(workflowDefinitionIssueSchema),
  /** Context fields the definition reads — what the feature module must supply when it starts one. */
  readsContextFields: z.array(z.string()),
});
export type WorkflowDefinitionValidation = z.infer<typeof workflowDefinitionValidationSchema>;

// ---------------------------------------------------------------------------
// Coverage — "would an approval find a flow?"
// ---------------------------------------------------------------------------

export const workflowCoverageQuerySchema = z.object({ entityType: workflowEntityTypeSchema });

/** A finding in the shape of a payroll diagnostic, for the entity types that have one. */
export const workflowCoverageFindingSchema = z.object({
  ruleCode: z.string(),
  diagnosticClass: z.enum(PAYROLL_DIAGNOSTIC_CLASSES),
  severity: z.enum(PAYROLL_DIAGNOSTIC_SEVERITIES),
  title: z.string(),
  detail: z.string(),
  suggestedRemediation: z.string().nullable(),
});
export type WorkflowCoverageFinding = z.infer<typeof workflowCoverageFindingSchema>;

export const workflowResolutionSchema = z.object({
  definitionId: uuidSchema,
  version: z.number().int().min(1),
  /** `entity` = the legal entity's own definition, `org` = the organisation-wide fallback. */
  via: z.enum(['entity', 'org']),
});

export const workflowCoverageEntitySchema = z.object({
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  /** What the engine would pick for this entity, or null: starting an approval would 412. */
  resolution: workflowResolutionSchema.nullable(),
  finding: workflowCoverageFindingSchema.nullable(),
});

export const workflowCoverageSchema = z.object({
  entityType: workflowEntityTypeSchema,
  /**
   * Whether the code that starts this entity type's approval passes the legal entity to the
   * engine. When false, only the organisation-wide definition is ever consulted and a
   * legal-entity-scoped one is INERT — the coverage below reflects that, not the stored rows.
   */
  entityScopeConsulted: z.boolean(),
  entities: z.array(workflowCoverageEntitySchema),
});
export type WorkflowCoverage = z.infer<typeof workflowCoverageSchema>;

// ---------------------------------------------------------------------------
// Entity types — what CAN have an approval flow
// ---------------------------------------------------------------------------

/** How the editor should render a value input for a context field. */
export const WORKFLOW_CONTEXT_FIELD_KINDS = [
  'string',
  'number',
  /** Money or another exact quantity: a decimal STRING on the context (invariant 2). */
  'decimal',
  'boolean',
  /** `YYYY-MM-DD`. */
  'date',
  /** The uuid of another record — a leave type, a department. */
  'id',
] as const;
export type WorkflowContextFieldKind = (typeof WORKFLOW_CONTEXT_FIELD_KINDS)[number];

export const workflowContextFieldSchema = z.object({
  /** The context key a condition's `field` names, e.g. `amount`. */
  field: z.string(),
  label: z.string(),
  kind: z.enum(WORKFLOW_CONTEXT_FIELD_KINDS),
});
export type WorkflowContextField = z.infer<typeof workflowContextFieldSchema>;

/**
 * One kind of record some module sends for approval. Derived from the code that starts approvals,
 * so it lists exactly the types a definition can govern — a definition for anything else is inert.
 */
export const workflowApprovalTypeSchema = z.object({
  entityType: workflowEntityTypeSchema,
  /** Human label, e.g. "Leave request". */
  label: z.string(),
  /** The module that starts it, e.g. "Leave", "Payroll" — for grouping in the editor. */
  module: z.string(),
  /** False: only an organisation-wide definition is ever used — see `workflowCoverageSchema`. */
  entityScopeConsulted: z.boolean(),
  /**
   * The fields a condition may read: exactly what the module puts on the context when it starts an
   * approval. A condition reading anything else is refused on save (`unknown_context_field`),
   * because the engine would refuse to start every approval of this type.
   */
  contextFields: z.array(workflowContextFieldSchema),
});
export type WorkflowApprovalType = z.infer<typeof workflowApprovalTypeSchema>;

export const listWorkflowApprovalTypesResponseSchema = z.object({
  items: z.array(workflowApprovalTypeSchema),
});

// ---------------------------------------------------------------------------
// The router
// ---------------------------------------------------------------------------

export const workflowDefinitionsContract = c.router(
  {
    /** Static, so declared before `/:definitionId`. */
    coverage: {
      method: 'GET',
      path: '/coverage',
      query: workflowCoverageQuerySchema,
      responses: { 200: workflowCoverageSchema, ...authErrors },
      summary:
        'For an entity type: which legal entities would find an approval flow, and which would not',
      metadata: routeMeta('workflow:read'),
    },

    /** Static, so declared before `/:definitionId`. */
    listEntityTypes: {
      method: 'GET',
      path: '/entity-types',
      responses: { 200: listWorkflowApprovalTypesResponseSchema, ...authErrors },
      summary:
        'The kinds of request that can have an approval flow, and the fields each flow may test',
      metadata: routeMeta('workflow:read'),
    },

    listDefinitions: {
      method: 'GET',
      path: '/',
      query: listWorkflowDefinitionsQuerySchema,
      responses: { 200: listWorkflowDefinitionsResponseSchema, ...authErrors },
      summary:
        "The organisation's approval workflow definitions — the latest version of each, or all",
      metadata: routeMeta('workflow:read'),
    },

    createDefinition: {
      method: 'POST',
      path: '/',
      headers: idempotencyHeadersSchema,
      body: createWorkflowDefinitionBodySchema,
      responses: {
        201: workflowDefinitionSchema,
        ...authErrors,
        /** `details` is `workflowDefinitionExistsSchema`: edit that lineage instead. */
        409: errorBodySchema,
        /** The definition would not run, or names an unknown role or legal entity. */
        422: errorBodySchema,
      },
      summary:
        'Create the first version of an approval flow for a request type, organisation-wide or for one legal entity',
      metadata: routeMeta('workflow:manage'),
    },

    validateDefinition: {
      method: 'POST',
      path: '/validate',
      body: validateWorkflowDefinitionBodySchema,
      responses: { 200: workflowDefinitionValidationSchema, ...authErrors },
      summary: 'Check a definition against the engine without saving it',
      // AI-hidden: a non-GET would otherwise be offered to the copilot as a write proposal, and
      // this writes nothing. Same reasoning as `payrollRules.simulate`.
      metadata: routeMeta('workflow:manage', { ai: 'hidden' }),
    },

    getDefinition: {
      method: 'GET',
      path: '/:definitionId',
      pathParams: workflowDefinitionParamsSchema,
      responses: { 200: workflowDefinitionSchema, ...authErrors, 404: errorBodySchema },
      summary: 'One version of a workflow definition',
      metadata: routeMeta('workflow:read'),
    },

    updateDefinition: {
      method: 'PUT',
      path: '/:definitionId',
      pathParams: workflowDefinitionParamsSchema,
      headers: idempotencyHeadersSchema,
      body: updateWorkflowDefinitionBodySchema,
      responses: {
        200: workflowDefinitionSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `details` is `workflowDefinitionConflictSchema`: a newer version exists. */
        409: errorBodySchema,
        /** The definition would not run: the engine's own validation failed. */
        422: errorBodySchema,
      },
      summary:
        'Save the next version of a definition, based on the version the caller loaded — never edited in place',
      metadata: routeMeta('workflow:manage'),
    },

    /**
     * Switch a lineage off: the next approval of its type finds no flow here (and falls back to the
     * organisation-wide one, if this was a legal entity's). Written as the NEXT version with the
     * same steps and `isActive: false`, so the history shows when and by whom — never an edit in
     * place. Approvals already running are untouched. `:definitionId` is the version the caller
     * loaded; a stale one is a 409 (`workflowDefinitionConflictSchema`). Already off = no-op.
     */
    deactivateDefinition: {
      method: 'POST',
      path: '/:definitionId/deactivate',
      pathParams: workflowDefinitionParamsSchema,
      headers: idempotencyHeadersSchema,
      body: c.noBody(),
      responses: {
        200: workflowDefinitionSchema,
        ...authErrors,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Turn an approval flow off, as a new version; approvals already running are unaffected',
      metadata: routeMeta('workflow:manage'),
    },

    /** The inverse of `deactivateDefinition`, with the same versioning and 409. Already on = no-op. */
    reactivateDefinition: {
      method: 'POST',
      path: '/:definitionId/reactivate',
      pathParams: workflowDefinitionParamsSchema,
      headers: idempotencyHeadersSchema,
      body: c.noBody(),
      responses: {
        200: workflowDefinitionSchema,
        ...authErrors,
        404: errorBodySchema,
        409: errorBodySchema,
        /** The stored steps no longer pass — e.g. a role they name has since been deleted. */
        422: errorBodySchema,
      },
      summary: 'Turn an approval flow back on, as a new version',
      metadata: routeMeta('workflow:manage'),
    },
  },
  { pathPrefix: '/workflow-definitions' },
);
